import type { LatLon, SerpentineRun, Stage, StageRoute } from './types'

/**
 * Alles, was an der berechneten Strecke zu entscheiden ist: was gespeichert
 * wird, was vorgeschlagen wird, was gezeichnet wird.
 *
 * Reine Funktionen und kein React, kein fetch, keine Karte. Der Netzaufruf
 * steht in data/api.ts, die Bedienung in features/stages, das Zeichnen in
 * features/map - hier stehen nur die Regeln, und die sind ohne Browser
 * prüfbar.
 */

/**
 * Wie viele Stützpunkte je Etappe höchstens gespeichert werden.
 *
 * Die Geometrie liegt im Reisedokument, und das geht bei jeder Änderung als
 * Ganzes durch den Abgleich und in die Exportdatei. Auf einer Karte, die 500
 * Kilometer am Stück zeigt, ist der Strassenverlauf auf zehn Meter genau
 * nichts wert; ein paar hundert Punkte je Etappe zeichnen dieselbe Linie.
 */
export const MAX_GEOMETRY_POINTS = 400

/**
 * Fassung des Verfahrens, mit dem eine gespeicherte Strecke entstanden ist.
 *
 * 1 (im Feld nicht vermerkt) ist die erste Fassung: der Dienst wurde nach einer
 * bereits geglätteten Linie gefragt, und ausgedünnt wurde in gleichmässigen
 * Abständen. 2 fragt die volle Geometrie an und wählt nach Bedeutung aus, siehe
 * thinGeometry. 3 zählt zusätzlich die Kehren, bevor ausgedünnt wird, und legt
 * das Ergebnis in der Strecke ab (serpentineRuns).
 *
 * Der Vermerk steht in der Strecke, weil man ihr sonst nicht ansieht, wie sie
 * entstanden ist: eine grob gezeichnete Kehre ist eine gültige Linie zwischen
 * den richtigen Koordinaten, routeState hat also nichts zu beanstanden. Ohne
 * das Feld bliebe jede vor der Umstellung berechnete Etappe für immer grob.
 *
 * Warum die Kehren die Fassung hochzählen, obwohl die Linie dieselbe bleibt:
 * die Zahl lässt sich nachträglich nicht mehr gewinnen. Ausgedünnt sind von den
 * 3501 Stützpunkten des Vršič noch 400 übrig, und wo vorher fünf Punkte eine
 * Haarnadel beschrieben, steht danach einer. Eine Strecke ohne Kehrenzahl ist
 * deshalb keine Strecke, deren Kehrenzahl null ist - sie ist eine, die nie
 * gezählt wurde, und nur die Fassung sagt den Unterschied.
 */
export const GEOMETRY_DETAIL = 3

/**
 * Ab wie viel Drehung eine Richtungsänderung eine Kehre ist, in Grad.
 *
 * Hundertfünfunddreissig, also drei Achtel des vollen Kreises. Die Zahl folgt
 * aus der Notation selbst: sie liegt auf dem Achtelraster (45 Grad, siehe
 * TULIP_SHAPES), und die Stufen darunter haben bereits ihre eigenen Zeichen -
 * neunzig Grad ist eine Kreuzung oder ein Abzweig, kein Zickzack. Eine Kehre
 * ist das, wofür es kein Kreuzungszeichen gibt: die Strasse dreht um und läuft
 * zurück, wo sie hergekommen ist.
 *
 * Gemessen an der Wirklichkeit: der Vršič hat laut Reiseführer 24 Kehren hinauf
 * und 26 hinunter. Bei 135 Grad findet dieses Verfahren 25 und 22, also 47
 * statt 50 - sechs Prozent zu wenig. Der Rest sind die weiten Kehren, die zwar
 * einen Stein mit Nummer haben, aber nur um 110 bis 130 Grad drehen. Die
 * Schwelle auf 120 zu senken holte sie herein (58 statt 50, zwölf Prozent zu
 * viel) und nähme dafür jede Autobahnschleife mit. Lieber sechs Prozent zu
 * wenig als eine Kehre, die keine ist: im Buch steht am Ende eine Zahl neben
 * einem Zeichen, und wer 58 liest und 50 fährt, glaubt beim nächsten Mal keiner
 * Zahl mehr.
 */
export const SERPENTINE_DEGREES = 135

/**
 * Auf welcher Strecke diese Drehung stattfinden muss, in Metern.
 *
 * Hundert Meter. Eine Haarnadel im Gebirge hat innen zehn bis fünfzehn Meter
 * Radius; drei Achtel eines solchen Kreises sind fünfundzwanzig bis fünfunddreissig
 * Meter Fahrbahn, dazu je eine Wagenlänge Zufahrt und Ausfahrt, die in den
 * Kartendaten noch zur Biegung gehören. Hundert Meter fassen das mit Rand.
 *
 * Nach oben begrenzt die Zahl das, was keine Kehre mehr ist: eine Talstrasse
 * dreht auf ihrem Weg um den Berg auch um 135 Grad, nur braucht sie dafür
 * dreihundert bis fünfhundert Meter. Das ist eine Kurve, und im Auto merkt man
 * den Unterschied am Lenkrad.
 */
export const SERPENTINE_WITHIN_M = 100

/**
 * Wie viel Gegendrehung innerhalb einer Kehre hingenommen wird, in Grad.
 *
 * Fünfzehn. Eine Haarnadel ist in den Kartendaten kein sauberer Kreisbogen: die
 * Stützpunkte wackeln um ein, zwei Grad, und am Scheitel liegt gern einer
 * daneben. Ohne diese Nachsicht risse die Zählung mitten in der Kehre ab und
 * fände zwei halbe statt einer ganzen. Grösser darf sie nicht sein, sonst
 * verschmilzt eine Rechts- mit der folgenden Linkskehre zu einer Drehung, die
 * es nicht gibt.
 */
const SERPENTINE_GEGEN = 15

/**
 * Wie weit zwei Kehren auseinanderliegen dürfen und noch dieselbe Folge sind,
 * in Metern.
 *
 * Fünfzehnhundert. Das ist die Zahl, die aus 47 einzelnen Kehren die zwei
 * Zeilen macht, die im Buch stehen sollen: am Vršič ist die längste Gerade
 * innerhalb einer Rampe 1059 Meter, zwischen der Auffahrt und der Abfahrt
 * liegen 1938. Mit 2000 wird daraus eine einzige Zeile über 15 Kilometer, mit
 * 1000 werden es vier. Der Wert liegt also nicht in der Mitte, sondern dort, wo
 * die Daten einen Sprung machen.
 *
 * Dahinter steht eine Fahrerregel: anderthalb Kilometer sind bei vierzig
 * Stundenkilometern gut zwei Minuten ohne Haarnadel. Wer so lange geradeaus
 * fährt, ist aus der Kehrenfolge heraus - und eine Zeile im Roadbook soll ein
 * Stück Strasse beschreiben, das man am Stück fährt.
 */
export const SERPENTINE_GAP_M = 1500

/**
 * Wie viele Kehren eine Folge mindestens hat.
 *
 * Drei. Zwei aufeinanderfolgende Haarnadeln sind eine S-Kurve und stehen in
 * jedem Dorf; ab der dritten fährt man eine Rampe. Der Deckel ist ausserdem
 * das, was die Autobahnprobe bestehen lässt: eine Autobahnschleife dreht um 180
 * Grad und wird als einzelne Kehre gefunden - allein steht sie, und allein
 * kommt sie nicht ins Buch.
 */
export const SERPENTINE_MIN = 3

/** Punkte, die näher beieinander liegen, geben keine Richtung her (Meter). */
const SERPENTINE_MIN_SEG_M = 1

/** Mittlerer Erdradius in Metern. */
const ERDRADIUS_M = 6371008.8

/**
 * Meter zwischen zwei Koordinaten, in der Ebene gerechnet.
 *
 * Für Abstände von wenigen hundert Metern reicht das: der Fehler gegenüber der
 * Kugelrechnung liegt bei einem Kilometer im Millimeterbereich. Die Länge quer
 * wird mit dem Kosinus der mittleren Breite gestaucht, aus demselben Grund wie
 * in querAbstand - ein Längengrad misst bei 46 Grad Nord nur gut zwei Drittel
 * eines Breitengrades.
 */
function meter(a: LatLon, b: LatLon): number {
  const mitte = (((a[0] + b[0]) / 2) * Math.PI) / 180
  const dx = (((b[1] - a[1]) * Math.PI) / 180) * Math.cos(mitte)
  const dy = ((b[0] - a[0]) * Math.PI) / 180
  return Math.hypot(dx, dy) * ERDRADIUS_M
}

/** Fahrtrichtung von a nach b, in Grad; 0 ist Nord. */
function richtung(a: LatLon, b: LatLon): number {
  const mitte = (((a[0] + b[0]) / 2) * Math.PI) / 180
  const dx = (((b[1] - a[1]) * Math.PI) / 180) * Math.cos(mitte)
  const dy = ((b[0] - a[0]) * Math.PI) / 180
  return (Math.atan2(dx, dy) * 180) / Math.PI
}

/** Bringt einen Winkelunterschied auf -180 bis 180. */
function drehung(grad: number): number {
  const v = ((grad % 360) + 360) % 360
  return v > 180 ? v - 360 : v
}

/**
 * Die Kehrenfolgen einer Strecke, in Fahrreihenfolge.
 *
 * Reine Rechnung auf der Linie, ohne fremde Quelle: eine Kehre ist eine
 * Richtungsänderung über einen grossen Winkel auf kurzer Distanz, und beides
 * steht in der Geometrie. Was der Routendienst dazu sagt, wird nicht gebraucht
 * - in keiner Abbiegeanweisung steht „Haarnadel“, und in den Manövern des
 * Vršič steht überhaupt nichts: dreiunddreissig Schritte für fünfzig Kehren,
 * weil die Passstrasse durchgehend dieselbe Strasse ist und ihre Kurven keine
 * Kreuzungen sind.
 *
 * Gerechnet werden muss auf der **vollen** Antwort des Dienstes. Nach
 * thinGeometry sind von 3501 Stützpunkten noch 400 übrig; Douglas-Peucker hält
 * zwar an jeder Haarnadel einen Punkt fest, aber aus einem Punkt lässt sich
 * keine Drehung über hundert Meter ablesen. Deshalb steht der Aufruf in
 * stageRouteFrom vor dem Ausdünnen und nicht danach.
 *
 * Das Verfahren geht die Linie einmal durch. An jedem Stützpunkt beginnt ein
 * Fenster, das so lange Drehung aufsammelt, wie sie in dieselbe Richtung geht
 * und die Strecke unter SERPENTINE_WITHIN_M bleibt. Kommt dabei
 * SERPENTINE_DEGREES zusammen, ist es eine Kehre, und die Suche setzt hinter
 * ihr fort - sonst zählte dieselbe Haarnadel an jedem ihrer Stützpunkte noch
 * einmal.
 *
 * @returns Nur Folgen, keine einzelnen Kehren. Eine leere Liste ist die
 *   richtige Antwort für eine Autobahn und für die meisten Etappen dieser
 *   Reise; gemessen an den sechzehn echten Etappen bleiben acht Folgen übrig.
 */
export function serpentineRuns(geometry: readonly LatLon[]): SerpentineRun[] {
  const kehren = einzelneKehren(geometry)
  const out: SerpentineRun[] = []

  let von = 0
  while (von < kehren.length) {
    let bis = von
    while (bis + 1 < kehren.length && kehren[bis + 1]!.von - kehren[bis]!.bis <= SERPENTINE_GAP_M) {
      bis++
    }
    const anzahl = bis - von + 1
    if (anzahl >= SERPENTINE_MIN) {
      const anfang = kehren[von]!.von
      out.push({
        km: Math.round(anfang) / 1000,
        count: anzahl,
        // Von der ersten bis zur letzten Kehre. Eine Nachkommastelle, weil
        // feiner niemand fährt - und weil die Zahl im Buch neben „25 Kehren“
        // steht und nicht in einer Messtabelle.
        lengthKm: Math.round((kehren[bis]!.bis - anfang) / 100) / 10,
      })
    }
    von = bis + 1
  }
  return out
}

/** Eine einzelne Kehre: wo sie anfängt und aufhört, in Metern ab Streckenbeginn. */
interface Kehre {
  von: number
  bis: number
}

/**
 * Die einzelnen Kehren, bevor sie zu Folgen zusammengefasst werden.
 *
 * Zuerst werden Stützpunkte übersprungen, die weniger als einen Meter
 * auseinanderliegen: aus ihnen lässt sich keine Fahrtrichtung ablesen, und ihr
 * Rauschen zählte als Drehung mit. Der Dienst liefert solche Paare regelmässig
 * dort, wo mehrere Wege denselben Knoten teilen.
 */
function einzelneKehren(geometry: readonly LatLon[]): Kehre[] {
  const punkte: LatLon[] = []
  const stand: number[] = []
  let gefahren = 0
  let letzter: LatLon | null = null

  for (const punkt of geometry) {
    if (!isCoord(punkt)) continue
    if (letzter) gefahren += meter(letzter, punkt)
    letzter = punkt
    const behalten = punkte[punkte.length - 1]
    if (!behalten || meter(behalten, punkt) >= SERPENTINE_MIN_SEG_M) {
      punkte.push(punkt)
      stand.push(gefahren)
    }
  }
  if (punkte.length < 4) return []

  // dreh[i] ist die Drehung am Punkt i+1, also zwischen dem Stück davor und dem
  // Stück danach.
  const dreh: number[] = []
  let vorher = richtung(punkte[0]!, punkte[1]!)
  for (let i = 1; i + 1 < punkte.length; i++) {
    const jetzt = richtung(punkte[i]!, punkte[i + 1]!)
    dreh.push(drehung(jetzt - vorher))
    vorher = jetzt
  }

  const out: Kehre[] = []
  let i = 0
  while (i < dreh.length) {
    const start = dreh[i]!
    if (start === 0) {
      i++
      continue
    }
    const sinn = start > 0 ? 1 : -1
    let summe = 0
    let treffer: Kehre | null = null

    for (let j = i; j < dreh.length; j++) {
      const d = dreh[j]!
      // Eine echte Gegendrehung beendet die Kehre: ab hier biegt die Strasse
      // wieder zurück, und was danach kommt, ist die nächste Haarnadel.
      if (d * sinn < 0 && Math.abs(d) > SERPENTINE_GEGEN) break
      summe += d
      // Gemessen wird vom Punkt vor der Drehung bis zu dem dahinter: das ist
      // die Fahrbahn, die durch die Biegung führt, samt ihrer Zufahrt.
      const spanne = stand[j + 2]! - stand[i]!
      if (spanne > SERPENTINE_WITHIN_M) break
      if (Math.abs(summe) >= SERPENTINE_DEGREES) {
        treffer = { von: stand[i]!, bis: stand[j + 2]! }
      }
    }

    if (!treffer) {
      i++
      continue
    }
    out.push(treffer)
    // Hinter das Ende springen. Ohne das fände dieselbe Haarnadel sich an jedem
    // ihrer Stützpunkte wieder, und aus einer Kehre würden fünf.
    while (i < dreh.length && stand[i + 1]! < treffer.bis) i++
  }
  return out
}

/**
 * Wie viele Zwischenpunkte eine Etappe höchstens tragen darf.
 *
 * Derselbe Wert wie im Server (MaxVia in backend/internal/routing/routing.go),
 * und er steht hier noch einmal, damit die Oberfläche gar nicht erst anbietet,
 * was hinterher als Absage zurückkäme. Zehn ist reichlich: ein Zwischenpunkt
 * zwingt die Strecke über eine bestimmte Strasse, dafür braucht es die
 * Passhöhe und nicht jede Kehre.
 */
export const MAX_VIA = 10

/**
 * Abstand, bis zu dem zwei Koordinaten als dieselbe gelten (in Grad).
 *
 * Ein Zehntausendstel Grad sind ungefähr elf Meter, also weniger als die
 * Schrittweite der Koordinatenfelder in der Etappenbearbeitung: jede Änderung
 * von Hand fällt auf, gerundete Werte aus einer Importdatei nicht.
 */
const SAME_COORD = 1e-4

/**
 * Die Antwort des Routendienstes, soweit sie das Datenmodell angeht.
 *
 * Absichtlich nur diese drei Felder und nicht der ganze Rumpf aus data/api.ts:
 * dieses Modul soll vom API-Client nichts wissen. RouteResponse dort erweitert
 * diesen Typ, damit beide Seiten dieselbe Zusage beschreiben.
 */
export interface RouteAnswer {
  distanceKm: number
  durationH: number
  geometry: LatLon[]
}

/** Zustand der gespeicherten Strecke einer Etappe. */
export type RouteState =
  /** Keine oder keine brauchbare Strecke gespeichert. */
  | 'none'
  /** Strecke gespeichert und passt zu den Vorgaben der Etappe. */
  | 'current'
  /**
   * Strecke gespeichert, aber die Vorgaben haben sich seither geändert: Start
   * oder Ziel wurden verschoben, oder die Zwischenpunkte sind andere.
   */
  | 'stale'

/** Liegen zwei Koordinaten so nah beieinander, dass es dieselbe ist? */
export function sameCoord(a: LatLon | undefined, b: LatLon | undefined): boolean {
  if (!a || !b) return false
  return Math.abs(a[0] - b[0]) <= SAME_COORD && Math.abs(a[1] - b[1]) <= SAME_COORD
}

/**
 * Die Zwischenpunkte einer Etappe als blosse Koordinaten, in Fahrreihenfolge.
 *
 * Das ist die Form, in der sie den Routendienst erreichen und in der sie in
 * der berechneten Strecke liegen bleiben: der Name bestimmt keinen Weg. Eine
 * Etappe ohne Zwischenpunkte ergibt eine leere Liste, und die ist überall
 * dasselbe wie „keine“.
 */
export function viaCoords(stage: Stage): LatLon[] {
  return (stage.via ?? []).map((punkt) => [punkt.at[0], punkt.at[1]])
}

/**
 * Sind das dieselben Zwischenpunkte, in derselben Reihenfolge?
 *
 * Verglichen wird der Länge nach und dann Punkt für Punkt, mit derselben
 * Toleranz wie bei Start und Ziel. Die Reihenfolge zählt mit: dieselben zwei
 * Punkte in anderer Folge ergeben eine andere Fahrt und damit eine andere
 * Strecke.
 *
 * Fehlt eine der beiden Listen, gilt sie als leer - so sehen die Strecken aus,
 * die vor dieser Erweiterung berechnet wurden, und eine Etappe ohne
 * Zwischenpunkte passt weiterhin zu ihnen.
 */
export function sameVia(a: LatLon[] | undefined, b: LatLon[] | undefined): boolean {
  const links = a ?? []
  const rechts = b ?? []
  if (links.length !== rechts.length) return false
  return links.every((punkt, i) => sameCoord(punkt, rechts[i]))
}

/**
 * Gibt es an dieser Etappe überhaupt etwas zu berechnen?
 *
 * Ein Standtag hat Start und Ziel am selben Ort **und nichts dazwischen**. Den
 * Dienst danach zu fragen kostet eine Anfrage und liefert eine Strecke der
 * Länge null.
 *
 * Die zweite Hälfte des Satzes hat lange gefehlt, und damit galt jede Runde als
 * Standtag: Zuhause - Zofingen - Olten - Aarau - Zuhause endet dort, wo es
 * beginnt, und ist trotzdem eine Fahrt von achtzig Kilometern. Die
 * Zwischenziele sind der Beweis - sie stehen da, weil jemand über sie fahren
 * will, und über einen Ort zu fahren ist etwas anderes, als dazubleiben.
 * Gemeint war die Etappe, die eben erst angelegt wurde und deren Ziel noch auf
 * ihrem Start sitzt (insertStageAfter in lib/trip.ts); die hat keine
 * Zwischenziele und bleibt, was sie war.
 */
export function isDrive(stage: Stage): boolean {
  if ((stage.via?.length ?? 0) > 0) return true
  return !sameCoord(stage.c1, stage.c2)
}

/**
 * Die Punkte, die die Etappe selbst vorgibt: Start, Zwischenziele, Ziel.
 *
 * Das Gerüst der Fahrt und nicht ihr Verlauf - der steht in der berechneten
 * Strecke, und solange es keine gibt, ist dies alles, was über den Weg bekannt
 * ist. Überall dort, wo etwas eingerahmt oder ersatzweise gezeichnet wird,
 * gehören sie vollständig hinein: Start und Ziel allein sind bei einer Runde
 * ein einziger Punkt, und ein Ausschnitt darum zeigt einen Vorgarten statt
 * achtzig Kilometer Mittelland.
 */
export function stageAnchors(stage: Stage): LatLon[] {
  return [stage.c1, ...viaCoords(stage), stage.c2]
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function isCoord(value: unknown): value is LatLon {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    finite(value[0]) !== null &&
    finite(value[1]) !== null
  )
}

/**
 * Wonach sich die Bedeutung eines Stützpunktes bemisst.
 *
 * - `geo`: Paare aus [Breite, Länge]. Gemessen wird der Abstand quer zur
 *   Fahrtrichtung, in Grad Breite als Einheit; die Länge wird vorher mit dem
 *   Kosinus der mittleren Breite gestaucht, weil ein Längengrad bei 46 Grad
 *   Nord nur noch gut zwei Drittel eines Breitengrades misst. Ohne diese
 *   Stauchung zählte eine Ausbuchtung nach Osten weniger als dieselbe nach
 *   Norden, und Kurven verschwänden je nach Himmelsrichtung verschieden.
 * - `profile`: Paare aus [Kilometer, Meter]. Zwei Achsen, die nichts
 *   miteinander zu tun haben - ein Abstand quer dazu wäre eine Zahl ohne
 *   Bedeutung, und ihr Wert hinge daran, wie man Kilometer gegen Meter
 *   aufrechnet. Gemessen wird deshalb senkrecht: um wie viele Meter der Punkt
 *   über oder unter der Linie liegt, die seine Nachbarn ziehen. Das ist genau
 *   der Fehler, den man im Diagramm sieht.
 */
export type ThinMode = 'geo' | 'profile'

/** Abstand eines Punktes von der Sehne zwischen a und b. */
type Deviation = (
  p: readonly [number, number],
  a: readonly [number, number],
  b: readonly [number, number],
) => number

/**
 * Abweichung, unterhalb derer ein Punkt als auf der Sehne liegend gilt.
 *
 * Rechnen mit Fliesskommazahlen trifft die Null nicht: drei Punkte auf einer
 * Geraden ergeben Abstände in der Grössenordnung von 1e-13, und ohne diese
 * Schwelle ginge das Budget für Rundungsrauschen drauf statt für Kurven - eine
 * schnurgerade Autobahn bekäme wieder ihre vierhundert Stützpunkte.
 *
 * Die Werte liegen weit unter dem, was in den Daten überhaupt unterscheidbar
 * ist: 1e-7 Grad ist gut ein Zentimeter, die Koordinaten selbst stehen auf fünf
 * Nachkommastellen (etwa ein Meter). Beim Profil ist es ein Millimeter, bei
 * Höhen, die auf einen Dezimeter gerundet sind.
 */
const NOISE: Record<ThinMode, number> = { geo: 1e-7, profile: 1e-3 }

/**
 * Dünnt eine Punktfolge auf höchstens max Punkte aus - nach Bedeutung.
 *
 * Behalten wird, wo die Linie ihre Richtung ändert; weggeworfen wird, was
 * ohnehin auf der Verbindung seiner Nachbarn liegt. Eine schnurgerade Ausfahrt
 * kommt so mit zwei Punkten aus, und die gesparten stehen dort zur Verfügung,
 * wo sie gebraucht werden: an den fünfzig Kehren des Vršič. Gleichmässig
 * auszuwählen hiess dagegen, alle paar hundert Meter einen Punkt zu setzen, ob
 * dort eine Haarnadel liegt oder eine Gerade. Gemessen an der vollen Geometrie
 * kostet das bei gleichem Budget das Vier- bis Zwölffache des grössten Fehlers.
 *
 * Das Verfahren ist Douglas-Peucker, nur andersherum aufgezogen: statt einen
 * Grenzabstand vorzugeben und abzuwarten, wie viele Punkte übrig bleiben, wird
 * immer der Punkt mit der grössten Abweichung als Nächstes behalten, bis das
 * Budget aufgebraucht ist. So ist die Zahl der Punkte die Vorgabe und der
 * Fehler das Ergebnis, und nicht umgekehrt - eine Etappe darf im Reisedokument
 * nun einmal nur so viel Platz brauchen.
 *
 * Der erste und der letzte Punkt bleiben immer: die Enden sind die Anknüpfung
 * an Start und Ziel. Liegt alles dazwischen auf der Geraden, kommen genau diese
 * zwei zurück - mehr sagt eine Gerade nicht, und die frei gebliebenen Plätze
 * kosten niemanden etwas.
 *
 * Die Folge bleibt offen, welcher Art ihre Glieder sind, solange es Zahlenpaare
 * sind: dieselbe Auswahl dünnt die berechnete Strasse aus, den aufgezeichneten
 * Weg und dessen Höhenprofil (lib/gpx.ts). Was ein Paar bedeutet, entscheidet
 * mode - zwei Fassungen derselben Auswahl liefen mit der Zeit auseinander.
 */
export function thinGeometry<T extends readonly [number, number]>(
  points: T[],
  max = MAX_GEOMETRY_POINTS,
  mode: ThinMode = 'geo',
): T[] {
  if (max < 2) return points.length > 0 ? [points[0]!] : []
  if (points.length <= max) return points

  const last = points.length - 1
  const abstand: Deviation = mode === 'profile' ? hoehenAbstand : querAbstand(points)
  const schwelle = NOISE[mode]

  /**
   * Der Punkt zwischen from und to, der am weitesten von deren Sehne abliegt.
   *
   * null, wenn nichts dazwischen liegt oder alles Dazwischenliegende auf der
   * Sehne sitzt: solche Punkte zu behalten änderte an der Zeichnung nichts und
   * nähme einer echten Kurve den Platz weg.
   */
  const teilung = (from: number, to: number): Split | null => {
    const a = points[from]!
    const b = points[to]!
    let at = -1
    let dev = schwelle
    for (let i = from + 1; i < to; i++) {
      const d = abstand(points[i]!, a, b)
      if (d > dev) {
        dev = d
        at = i
      }
    }
    return at < 0 ? null : { from, to, at, dev }
  }

  const behalten = new Uint8Array(points.length)
  behalten[0] = 1
  behalten[last] = 1
  let frei = max - 2

  // Ein Haufen statt einer Liste: gesucht wird immer nur die grösste noch offene
  // Abweichung, und die Zahl der offenen Abschnitte wächst mit dem Budget.
  const haufen: Split[] = []
  const anfang = teilung(0, last)
  if (anfang) heapPush(haufen, anfang)

  while (frei > 0 && haufen.length > 0) {
    const s = heapPop(haufen)
    behalten[s.at] = 1
    frei--
    const links = teilung(s.from, s.at)
    if (links) heapPush(haufen, links)
    const rechts = teilung(s.at, s.to)
    if (rechts) heapPush(haufen, rechts)
  }

  const out: T[] = []
  for (let i = 0; i <= last; i++) {
    if (behalten[i]) out.push(points[i]!)
  }
  return out
}

/** Ein noch nicht ausgereizter Abschnitt und sein auffälligster Punkt. */
interface Split {
  /** Erster behaltener Punkt des Abschnitts. */
  from: number
  /** Letzter behaltener Punkt des Abschnitts. */
  to: number
  /** Der Punkt dazwischen mit der grössten Abweichung. */
  at: number
  /** Diese Abweichung, immer grösser als NOISE. */
  dev: number
}

function heapSwap(heap: Split[], i: number, j: number): void {
  const merk = heap[i]!
  heap[i] = heap[j]!
  heap[j] = merk
}

function heapPush(heap: Split[], s: Split): void {
  heap.push(s)
  let i = heap.length - 1
  while (i > 0) {
    const eltern = (i - 1) >> 1
    if (heap[eltern]!.dev >= heap[i]!.dev) break
    heapSwap(heap, eltern, i)
    i = eltern
  }
}

/** Nimmt den Abschnitt mit der grössten Abweichung. Nur bei nicht leerem Haufen. */
function heapPop(heap: Split[]): Split {
  const oben = heap[0]!
  const letztes = heap.pop()!
  if (heap.length > 0) {
    heap[0] = letztes
    let i = 0
    for (;;) {
      const links = 2 * i + 1
      const rechts = links + 1
      let gross = i
      if (links < heap.length && heap[links]!.dev > heap[gross]!.dev) gross = links
      if (rechts < heap.length && heap[rechts]!.dev > heap[gross]!.dev) gross = rechts
      if (gross === i) break
      heapSwap(heap, i, gross)
      i = gross
    }
  }
  return oben
}

/**
 * Der Abstand quer zur Strecke, für Paare aus [Breite, Länge].
 *
 * Gemessen wird zur Strecke und nicht zur unendlichen Geraden durch a und b.
 * Das ist der Unterschied, an dem eine Kehre hängt: fährt der Weg von a aus
 * weit hinaus und kehrt neben a wieder um, liegt der Umkehrpunkt fast auf der
 * Geraden durch a und b - quer gemessen wäre die Schleife also unbedeutend und
 * fiele als Erstes weg. Zur Strecke gemessen ist sie so bedeutend, wie sie
 * lang ist.
 *
 * Der Kosinus wird einmal für die ganze Folge bestimmt. Über eine Etappe von
 * fünfhundert Kilometern ändert er sich um wenige Prozent, und ein Faktor, der
 * innerhalb derselben Auswahl wandert, machte die Reihenfolge der Abweichungen
 * von der Reihenfolge der Punkte abhängig.
 */
function querAbstand(points: readonly (readonly [number, number])[]): Deviation {
  let summe = 0
  for (const p of points) summe += p[0]
  const laengenmass = Math.cos(((summe / points.length) * Math.PI) / 180)

  return (p, a, b) => {
    const px = p[1] * laengenmass
    const ax = a[1] * laengenmass
    const bx = b[1] * laengenmass
    const dx = bx - ax
    const dy = b[0] - a[0]
    const laenge2 = dx * dx + dy * dy
    if (laenge2 === 0) return Math.hypot(px - ax, p[0] - a[0])
    // t ist der Fusspunkt auf der Strecke, auf [0, 1] beschnitten: ausserhalb
    // zählt der Abstand zum näheren Ende.
    let t = ((px - ax) * dx + (p[0] - a[0]) * dy) / laenge2
    if (t < 0) t = 0
    else if (t > 1) t = 1
    return Math.hypot(px - (ax + t * dx), p[0] - (a[0] + t * dy))
  }
}

/**
 * Der senkrechte Abstand in Metern, für Paare aus [Kilometer, Meter].
 *
 * Ein Höhenprofil ist eine Funktion über der Strecke, kein Weg in der Ebene.
 * Bedeutend ist ein Punkt genau dann, wenn die gerade Verbindung seiner
 * Nachbarn ihn verfehlt - der Gipfel des Passes also, nicht der zwanzigste
 * Punkt einer gleichmässigen Steigung. Die Kilometerachse wächst dabei nie
 * rückwärts, der Fusspunkt liegt deshalb immer zwischen a und b.
 */
function hoehenAbstand(
  p: readonly [number, number],
  a: readonly [number, number],
  b: readonly [number, number],
): number {
  const spanne = b[0] - a[0]
  // Zwei Messungen am selben Kilometer: dann gibt es keine Linie, unter der der
  // Punkt liegen könnte, und es bleibt der Höhenunterschied selbst.
  if (spanne === 0) return Math.abs(p[1] - a[1])
  const t = (p[0] - a[0]) / spanne
  return Math.abs(p[1] - (a[1] + t * (b[1] - a[1])))
}

/**
 * Macht aus der Antwort des Dienstes das Feld, das in der Etappe liegen bleibt.
 *
 * Die Etappe geht als Ganzes hinein und nicht ihre Koordinaten einzeln: die
 * Strecke ist für genau diesen Start, dieses Ziel und diese Zwischenpunkte
 * gerechnet worden, und alle drei müssen mit ihr gespeichert werden. Wären es
 * drei Parameter, könnte ein Aufrufer den dritten vergessen - und eine Strecke
 * ohne ihre Zwischenpunkte gälte für immer als veraltet.
 *
 * @param answer Antwort des Dienstes.
 * @param stage Die Etappe, für die gefragt wurde.
 * @param at Zeitpunkt der Berechnung.
 * @returns null, wenn die Antwort keine brauchbare Strecke enthält. Eine Linie
 *   aus einem Punkt ist keine Strecke, und eine Entfernung, die keine Zahl ist,
 *   wäre in den Summen ein stiller Fehler. Auch ein einzelner unbrauchbarer
 *   Stützpunkt verwirft die ganze Antwort - ihn zu überspringen ergäbe eine
 *   Linie, die eine Abkürzung nimmt, die es auf der Strasse nicht gibt. Dieselbe
 *   Regel gilt beim Import, siehe lib/transfer.ts.
 */
export function stageRouteFrom(answer: RouteAnswer, stage: Stage, at: number): StageRoute | null {
  const distanceKm = finite(answer.distanceKm)
  const durationH = finite(answer.durationH)
  if (distanceKm === null || durationH === null || distanceKm < 0 || durationH < 0) return null

  const roh = Array.isArray(answer.geometry) ? answer.geometry : []
  const geometry: LatLon[] = []
  for (const point of roh) {
    if (!isCoord(point)) return null
    geometry.push([point[0], point[1]])
  }
  if (geometry.length < 2) return null

  // Gezählt wird vor dem Ausdünnen und nur hier: nach thinGeometry ist die
  // Haarnadel ein einzelner Punkt, und aus einem Punkt lässt sich keine Drehung
  // ablesen. Das Ergebnis liegt deshalb als Zahl in der Strecke und nicht als
  // Rechnung, die man später noch einmal anstellen könnte.
  const serpentines = serpentineRuns(geometry)

  const via = viaCoords(stage)
  return {
    geometry: thinGeometry(geometry),
    distanceKm,
    durationH,
    computedAt: at,
    detail: GEOMETRY_DETAIL,
    // Wie bei via: das Feld bleibt weg, wo es nichts zu merken gibt. Eine leere
    // Liste und ein fehlendes Feld sind nach dem Abgleich zwei verschiedene
    // Dokumente und doch dieselbe Auskunft - „auf dieser Strecke keine Kehren“.
    // Dass gezählt wurde, sagt schon detail.
    ...(serpentines.length > 0 ? { serpentines } : {}),
    from: [stage.c1[0], stage.c1[1]],
    // Das Feld bleibt weg, wo es nichts zu merken gibt: ein Dokument mit einer
    // leeren Liste sieht nach dem Abgleich anders aus als eines ohne das Feld -
    // und ist doch dasselbe. Genauso wie bei der Koordinate eines Platzes.
    ...(via.length > 0 ? { via } : {}),
    to: [stage.c2[0], stage.c2[1]],
  }
}

/**
 * Ist die gespeicherte Strecke mit einem älteren Verfahren entstanden?
 *
 * Sie ist deshalb nicht falsch, nur grob - sie wird weiter gezeichnet und
 * weiter gerechnet. Der Sammelaufruf (features/stages/stageRouting.ts) nimmt
 * sie aber mit, wenn er ohnehin läuft: neu berechnet kostet sie eine Anfrage,
 * und dieselbe Etappe sieht danach ihre Kehren.
 */
export function isCoarseRoute(stage: Stage): boolean {
  const route = stage.route
  if (!usable(route)) return false
  return (finite(route.detail) ?? 1) < GEOMETRY_DETAIL
}

/** Taugt die gespeicherte Strecke zum Zeichnen und Rechnen? */
function usable(route: StageRoute | undefined): route is StageRoute {
  return (
    !!route &&
    Array.isArray(route.geometry) &&
    route.geometry.length >= 2 &&
    finite(route.distanceKm) !== null &&
    finite(route.durationH) !== null
  )
}

/**
 * Sagt, woran man mit der gespeicherten Strecke einer Etappe ist.
 *
 * Die Reihenfolge der Prüfungen ist die Aussage: erst gibt es überhaupt
 * etwas, dann passt es noch zu den Koordinaten, dann zu den Zwischenpunkten.
 *
 * Die Zwischenpunkte zählen genauso wie das Ziel: wer den Vršič einträgt,
 * nachdem die Strecke schon berechnet war, hat eine Strecke im Dokument, die
 * durch den Karawankentunnel führt. Sie ist eine gültige Linie zwischen den
 * richtigen Koordinaten - man sieht ihr nicht an, dass sie nicht mehr die
 * gemeinte ist. Ohne diese Prüfung zeigte die Karte sie weiter, und der Export
 * fürs Navigationsgerät nähme sie mit.
 */
export function routeState(stage: Stage): RouteState {
  const route = stage.route
  if (!usable(route)) return 'none'
  if (!sameCoord(route.from, stage.c1) || !sameCoord(route.to, stage.c2)) return 'stale'
  if (!sameVia(route.via, viaCoords(stage))) return 'stale'
  return 'current'
}

/**
 * Die Punkte, die für diese Etappe gezeichnet werden dürfen.
 *
 * @returns null, wenn es keine aktuelle Strecke gibt - dann bleibt die
 *   Luftlinie. Eine veraltete Strecke wird ausdrücklich nicht gezeichnet: sie
 *   führt zu einem Ziel, das nicht mehr das Ziel ist.
 */
export function routeGeometry(stage: Stage): LatLon[] | null {
  if (routeState(stage) !== 'current') return null
  return stage.route?.geometry ?? null
}

/**
 * Fahrzeit mit dem Zuschlag des Fahrzeugs, auf eine Nachkommastelle gerundet.
 *
 * Der Zuschlag ist ein Pflichtwert und hat hier keinen Vorgabewert mehr. Er
 * stand als Konstante in dieser Datei - ein Viertel, für den beladenen T3 -,
 * und damit plante jede Reise wie ein Camper, auch die mit einem
 * Personenwagen. Er gehört ans Fahrzeug (siehe lib/vehicle.ts), und dass ihn
 * jeder Aufrufer mitbringen muss, ist die Zusage, dass er nirgends mehr
 * stillschweigend entsteht.
 */
export function vehicleHours(durationH: number, surcharge: number): number {
  const base = finite(durationH)
  if (base === null || base < 0) return 0
  const zuschlag = Math.max(0, finite(surcharge) ?? 0)
  return round1(base * (1 + zuschlag))
}

/** Was aus einer berechneten Strecke für die Planung vorgeschlagen wird. */
export interface RouteProposal {
  /** Entfernung, auf ganze Kilometer gerundet. */
  km: number
  /** Fahrzeit laut Dienst, also für einen Personenwagen. */
  carH: number
  /**
   * Dieselbe Fahrzeit mit dem Zuschlag des Fahrzeugs.
   *
   * Ohne Zuschlag ist sie carH. Das ist kein Sonderfall, sondern der Normalfall
   * für alles, was kein beladener Bus ist.
   */
  vehicleH: number
  /** Der angesetzte Zuschlag, damit die Oberfläche ihn benennen kann. */
  surcharge: number
}

/**
 * Der Vorschlag zu einer Strecke: gerundet, wie die Handwerte gerundet sind.
 *
 * Kilometer auf ganze, Stunden auf eine Nachkommastelle - genauer wäre in der
 * Planung eine Scheingenauigkeit, denn 548.3 km ist die Rechnung eines Dienstes
 * und nicht der Tachostand.
 */
export function routeProposal(route: StageRoute, surcharge: number): RouteProposal {
  const km = finite(route.distanceKm) ?? 0
  const carH = finite(route.durationH) ?? 0
  return {
    km: Math.round(km),
    carH: round1(carH),
    vehicleH: vehicleHours(carH, surcharge),
    surcharge: Math.max(0, finite(surcharge) ?? 0),
  }
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}
